import { useState, useEffect, useCallback } from "react";
import userServices from "../../api/services/userService";

const DEFAULT_LIMIT = 10;

export function useTopUsers() {
  const [activeTab, setActiveTab] = useState("all");
  const [users, setUsers] = useState([]);
  const [backendPage, setBackendPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [verifyModalOpen, setVerifyModalOpen] = useState(false);
  const [selectedUserForVerify, setSelectedUserForVerify] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setBackendPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setIsLoading(true);
        let result;
        if (activeTab === "all") {
          result = await userServices.fetchAllUsers(
            backendPage,
            debouncedSearch || null
          );
        } else if (activeTab === "completed") {
          result = await userServices.fetchCompletedUsers(
            backendPage,
            debouncedSearch || null
          );
        }
        const allUsers = result?.users || [];
        setUsers(allUsers);
        setTotal(result?.total ?? 0);
        setLimit(result?.limit ?? DEFAULT_LIMIT);
      } catch (error) {
        console.error("Error fetching users:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchUsers();
  }, [backendPage, activeTab, debouncedSearch]);

  const totalPages = Math.ceil(total / limit) || 1;
  const currentPage = backendPage;
  const startIndex = total === 0 ? 0 : (currentPage - 1) * limit + 1;
  const endIndex = Math.min(currentPage * limit, total);

  const handleNext = useCallback(() => {
    if (currentPage < totalPages) {
      setBackendPage((prev) => prev + 1);
    }
  }, [currentPage, totalPages]);

  const handlePrev = useCallback(() => {
    if (currentPage > 1) {
      setBackendPage((prev) => prev - 1);
    }
  }, [currentPage]);

  const openVerifyModal = useCallback((user) => {
    setSelectedUserForVerify(user);
    setVerifyModalOpen(true);
  }, []);

  const closeVerifyModal = useCallback(() => {
    setVerifyModalOpen(false);
    setSelectedUserForVerify(null);
  }, []);

  const setTab = useCallback((key) => {
    setActiveTab(key);
    setBackendPage(1);
  }, []);

  return {
    activeTab,
    setTab,
    users,
    searchQuery,
    setSearchQuery,
    isLoading,
    backendPage,
    backendLimit: limit,
    total,
    totalPages,
    currentPage,
    startIndex,
    endIndex,
    handleNext,
    handlePrev,
    verifyModalOpen,
    selectedUserForVerify,
    openVerifyModal,
    closeVerifyModal,
  };
}
